// My Trading Journey: one account's story (what it cost, what it paid, how long it lived, how it
// ended, the lesson, and every trade), and adding a past account from before Market Matrix, with
// what it cost, so the journey covers every year of trading.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { accountHistory, firmOf } from './business';
import { BLOWN_CAUSES, END_CAUSE_NAMES } from './journey';
import { dayKey, formatMoney, netPnl, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const whole = (n) => usd.format(Math.round(n)).replace(/\.00$/, '');
const dayFull = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', day: 'numeric', year: 'numeric' });
const OUTCOME = { failed: 'Blown', blown: 'Blown', passed: 'Passed', active: 'Active', ended: 'Ended', restarted: 'Restarted' };

function useDialog(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return ref;
}

// The running P&L of the account, trade by trade.
function Curve({ trades }) {
  const points = useMemo(() => {
    let run = 0;
    return [0, ...[...trades].sort((a, b) => new Date(a.date) - new Date(b.date)).map((t) => (run += netPnl(t)))];
  }, [trades]);
  if (points.length < 3) return null;
  const W = 600;
  const H = 120;
  const lo = Math.min(0, ...points);
  const hi = Math.max(0, ...points);
  const x = (i) => (i / (points.length - 1)) * W;
  const y = (v) => H - 6 - ((v - lo) / (hi - lo || 1)) * (H - 12);
  const d = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="ja-curve" aria-hidden="true">
      <line x1="0" x2={W} y1={y(0)} y2={y(0)} className="jo-grid" />
      <path d={d} className={`ja-curve-line ${last >= 0 ? 'gain' : 'loss'}`} />
    </svg>
  );
}

export function AccountStory({ accountId, accounts, trades, expenses, onClose, onOpenTrades }) {
  const { t } = useI18n();
  const ref = useDialog(onClose);
  const account = accounts.find((a) => String(a._id) === String(accountId));
  const h = useMemo(() => (account ? accountHistory([account], trades, expenses)[0] : null), [account, trades, expenses]);
  const own = useMemo(() => trades.filter((x) => String(x.accountId) === String(accountId)).sort((a, b) => new Date(b.date) - new Date(a.date)), [trades, accountId]);
  if (!account) return null;
  const pnl = own.reduce((s, x) => s + netPnl(x), 0);
  const outcome = h ? h.outcome : account.archived ? 'ended' : 'active';
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} tabIndex={-1} className="modal ja-modal" role="dialog" aria-modal="true" aria-labelledby="ja-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">{t('Account story')}</p>
            <h2 id="ja-title">{account.name}</h2>
            <p className="ja-sub">
              {[firmOf(account), t(account.type || 'Account'), account.size ? `${account.size / 1000}K` : ''].filter(Boolean).join(' · ')}
              {' · '}<span className={`jo-badge ${outcome}`}>{t(OUTCOME[outcome] || outcome)}</span>
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <div className="ja-tiles">
          <div className="jo-tile"><span className="jo-tile-label">{t('Cost')}</span><span className="jo-tile-value spent">{h && h.spent ? whole(h.spent) : '—'}</span></div>
          <div className="jo-tile"><span className="jo-tile-label">{t('Paid out')}</span><span className="jo-tile-value paid">{h && h.payouts ? whole(h.payouts) : '—'}</span></div>
          <div className="jo-tile"><span className="jo-tile-label">{t('Trading result')}</span><span className={`jo-tile-value ${pnl >= 0 ? 'paid' : 'spent'}`}>{formatMoney(pnl)}</span></div>
          <div className="jo-tile"><span className="jo-tile-label">{t('Lived')}</span><span className="jo-tile-value">{h ? t(h.days === 1 ? '{n} day' : '{n} days', { n: h.days }) : '—'}</span>
            {h && <span className="jo-tile-note">{dayFull(h.start)}{h.end ? ` → ${dayFull(h.end)}` : ''}</span>}</div>
        </div>
        {(account.endCause || account.attemptNote || account.lesson) && (
          <div className="ja-why">
            {account.endCause && <p><strong>{t('How it ended')}:</strong> {t(END_CAUSE_NAMES[account.endCause])}</p>}
            {account.attemptNote && <p className="ja-note">“{account.attemptNote}”</p>}
            {account.lesson && <p className="jo-lesson">💡 {t('Lesson')}: {account.lesson}</p>}
          </div>
        )}
        <Curve trades={own} />
        <h3 className="ja-h3">{t(own.length === 1 ? '{n} trade' : '{n} trades', { n: own.length })}</h3>
        {own.length ? (
          <div className="table-wrap ja-trades">
            <table className="biz-table">
              <thead><tr><th>{t('Date')}</th><th>{t('Symbol')}</th><th>{t('Side')}</th><th className="num">{t('Size')}</th><th className="num">{t('Net P&L')}</th></tr></thead>
              <tbody>
                {own.slice(0, 200).map((x) => (
                  <tr key={x._id}>
                    <td>{dayFull(dayKey(x.date))}</td><td>{x.symbol}</td><td>{t(x.side)}</td><td className="num">{x.size}</td>
                    <td className={`num ${netPnl(x) >= 0 ? 'gain' : 'loss'}`}>{formatMoney(netPnl(x))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="field-hint">{t('No trades in this account yet. Import its trade history from a CSV file to fill in its story.')}</p>}
        <div className="modal-actions">
          {onOpenTrades && <button type="button" className="secondary" onClick={() => onOpenTrades(account._id, false)}>{t('Open in Trades')}</button>}
          {onOpenTrades && <button type="button" onClick={() => onOpenTrades(account._id, true)}>{t('Import trades (CSV)')}</button>}
        </div>
      </div>
    </div>
  );
}

const FIRM_CHOICES = ['Topstep', 'Apex', 'MyFundedFutures', 'Lucid', 'Tradeify', 'TradeDay', 'Take Profit Trader', 'Bulenox'];

// A prop account (or personal account) from before Market Matrix: when it ran, how it ended,
// the lesson, and what it cost. Its trades can be imported from a CSV after.
export function PastAccountForm({ onSaved, onClose, onSessionExpired }) {
  const { t } = useI18n();
  const ref = useDialog(onClose);
  const [f, setF] = useState({ firm: 'Topstep', name: '', type: 'Evaluation', size: '50000', start: '', end: '', outcome: 'failed', cause: 'daily_loss', lesson: '', cost: '', payouts: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const isProp = f.type !== 'Personal';

  async function save(e) {
    e.preventDefault();
    if (!f.start || !f.end || f.end < f.start) return setError(t('Enter when the account started and ended.'));
    setSaving(true);
    setError('');
    try {
      const name = f.name.trim() || `${isProp ? `${f.firm} ` : ''}${f.size ? `${Number(f.size) / 1000}K` : ''} (${t('past')})`.trim();
      const account = await api('/accounts', {
        method: 'POST',
        body: {
          name, type: f.type, platform: f.firm === 'Topstep' ? 'TopstepX' : 'Other', size: Number(f.size) || undefined, archived: true,
          attemptStartedAt: f.start, attemptEndedAt: f.end,
          ...(isProp && ['failed', 'passed'].includes(f.outcome) ? { outcome: f.outcome } : {}),
          ...(isProp && f.outcome === 'failed' ? { endCause: f.cause } : {}),
          ...(isProp && f.outcome === 'went_live' ? { endCause: 'went_live' } : {}),
          ...(f.lesson.trim() ? { lesson: f.lesson.trim() } : {}),
        },
      });
      if ((f.type === 'Funded' || f.type === 'Live') && Number(f.payouts) > 0) {
        await api(`/accounts/${account._id}/cash`, { method: 'POST', body: { kind: 'withdrawal', amount: Number(f.payouts), date: f.end, note: t('Payouts') } });
      }
      if (Number(f.cost) > 0) {
        await api('/expenses', { method: 'POST', body: { date: f.start, amount: Number(f.cost), category: 'evaluation', firm: isProp ? f.firm : '', accountId: account._id } });
      }
      onSaved(account);
    } catch (err) {
      if (err.status === 401) return onSessionExpired && onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form ref={ref} tabIndex={-1} className="modal ja-modal" onSubmit={save} role="dialog" aria-modal="true" aria-labelledby="pa-title">
        <div className="modal-header">
          <div>
            <h2 id="pa-title">{t('Add a past account')}</h2>
            <p className="field-hint">{t('An account from before you used Market Matrix. After saving, you can import its trades from a CSV file.')}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <div className="fields">
          <label>{t('Type')}
            <select name="type" value={f.type} onChange={set}>
              <option value="Evaluation">{t('Evaluation')}</option><option value="Funded">{t('Funded')}</option><option value="Live">{t('Live')}</option><option value="Personal">{t('Personal')}</option>
            </select>
          </label>
          {isProp && (
            <label>{t('Prop firm')}
              <select name="firm" value={f.firm} onChange={set}>{FIRM_CHOICES.map((x) => <option key={x}>{x}</option>)}</select>
            </label>
          )}
          <label>{t('Account size')}
            <input name="size" type="number" min="0" step="1000" value={f.size} onChange={set} />
          </label>
          <label>{t('Name (optional)')}
            <input name="name" value={f.name} onChange={set} maxLength={60} placeholder={t('e.g. Topstep 50K, March 2024')} />
          </label>
          <label>{t('Started')}<input name="start" type="date" value={f.start} onChange={set} required /></label>
          <label>{t('Ended')}<input name="end" type="date" value={f.end} onChange={set} required /></label>
          {isProp && (
            <label>{t('How it ended')}
              <select name="outcome" value={f.outcome} onChange={set}>
                <option value="failed">{t('Blown')}</option>
                {f.type === 'Evaluation' && <option value="passed">{t('Passed')}</option>}
                {f.type === 'Funded' && <option value="went_live">{t('Moved to live')}</option>}
                <option value="closed">{t('Closed')}</option>
              </select>
            </label>
          )}
          {isProp && f.outcome === 'failed' && (
            <label>{t('Cause')}
              <select name="cause" value={f.cause} onChange={set}>{BLOWN_CAUSES.map((c) => <option key={c} value={c}>{t(END_CAUSE_NAMES[c])}</option>)}</select>
            </label>
          )}
          <label>{t('What it cost (USD)')}<input name="cost" type="number" min="0" step="any" value={f.cost} onChange={set} placeholder={t('e.g. 165')} /></label>
          {(f.type === 'Funded' || f.type === 'Live') && <label>{t('Payouts received (USD)')}<input name="payouts" type="number" min="0" step="any" value={f.payouts} onChange={set} placeholder="0" /></label>}
        </div>
        <label>{t('Lesson (optional)')}
          <input name="lesson" value={f.lesson} onChange={set} maxLength={300} placeholder={t('e.g. Never more than 2 contracts on a new account.')} />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={onClose}>{t('Cancel')}</button>
          <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Save past account')}</button>
        </div>
      </form>
    </div>
  );
}
